import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import ArticleIcon from "@mui/icons-material/Article";
import DraftsIcon from "@mui/icons-material/Drafts";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import ViewListIcon from "@mui/icons-material/ViewList";
import ArchiveIcon from "@mui/icons-material/Archive";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
  showCount?: boolean;
}

interface HybridNavProps {
  children: React.ReactNode;
  currentPath?: string;
  onNavigate?: (path: string) => void;
}

const HybridNav: React.FC<HybridNavProps> = ({ children, currentPath, onNavigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    drafts: 0,
    inReview: 0,
    readyToPublish: 0,
    published: 0,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close drawer on mobile when path changes
  useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [currentPath, isMobile, mobileOpen]);

  // Load counts for navigation badges using heartbeat API
  const loadCounts = React.useCallback(async () => {
    if (!user) return;

    try {
      const result = await api.heartbeat();

      if (result.success && result.data) {
        console.log("[HybridNav] Heartbeat response:", {
          role: result.data.userRole,
          draftsCount: result.data.draftsCount,
          inReviewCount: result.data.inReviewCount,
          approvedCount: result.data.approvedCount,
          publishedCount: result.data.publishedCount,
        });

        setCounts({
          drafts: result.data.draftsCount,
          inReview: result.data.inReviewCount,
          readyToPublish: result.data.approvedCount || 0,
          published: result.data.publishedCount || 0,
        });
      } else {
        console.error("Failed to load heartbeat:", result.error);
      }
    } catch (error) {
      console.error("Failed to load counts:", error);
    }
  }, [user]);

  // Use ref to store the latest loadCounts function
  const loadCountsRef = React.useRef(loadCounts);
  React.useEffect(() => {
    loadCountsRef.current = loadCounts;
  }, [loadCounts]);

  // Set up interval only once
  useEffect(() => {
    // Initial load
    loadCountsRef.current();

    // Reload counts every 30 seconds
    const interval = setInterval(() => {
      loadCountsRef.current();
    }, 30000);

    return () => clearInterval(interval);
  }, []); // Empty dependency array - only run once on mount

  // Expose loadCounts to parent component
  useEffect(() => {
    // Store the loadCounts function reference in parent
    (window as any).__reloadCounts = loadCounts;
  }, [loadCounts]);

  // Role-based navigation structure with action-oriented labels
  const getNavItems = (): NavItem[] => {
    const role = user?.role;

    if (role === "reporter") {
      return [
        {
          label: "Report News",
          icon: <ArticleIcon />,
          path: "news-submission",
          roles: ["reporter"],
        },
        {
          label: "My Drafts",
          icon: <DraftsIcon />,
          path: "drafts",
          roles: ["reporter"],
          showCount: true,
        },
        {
          label: "Awaiting Review",
          icon: <RateReviewIcon />,
          path: "in-review",
          roles: ["reporter"],
          showCount: true,
        },
        {
          label: "Awaiting Publish",
          icon: <CheckCircleIcon />,
          path: "ready-to-publish",
          roles: ["reporter"],
          showCount: true,
        },
        {
          label: "Published",
          icon: <NewspaperIcon />,
          path: "live",
          roles: ["reporter"],
          showCount: true,
        },
      ];
    }

    if (role === "co-editor") {
      return [
        {
          label: "Report News",
          icon: <ArticleIcon />,
          path: "news-submission",
          roles: ["co-editor"],
        },
        {
          label: "Drafts",
          icon: <DraftsIcon />,
          path: "drafts",
          roles: ["co-editor"],
          showCount: true,
        },
        {
          label: "Awaiting Review",
          icon: <RateReviewIcon />,
          path: "in-review",
          roles: ["co-editor"],
          showCount: true,
        },
        {
          label: "Awaiting Publish",
          icon: <CheckCircleIcon />,
          path: "ready-to-publish",
          roles: ["co-editor"],
          showCount: true,
        },
        {
          label: "Published",
          icon: <NewspaperIcon />,
          path: "live",
          roles: ["co-editor"],
          showCount: true,
        },
      ];
    }

    if (role === "chief-editor" || role === "admin") {
      return [
        {
          label: "Report News",
          icon: <ArticleIcon />,
          path: "news-submission",
          roles: ["chief-editor", "admin"],
        },
        {
          label: "Drafts",
          icon: <DraftsIcon />,
          path: "drafts",
          roles: ["chief-editor", "admin"],
          showCount: true,
        },
        {
          label: "Awaiting Review",
          icon: <RateReviewIcon />,
          path: "in-review",
          roles: ["chief-editor", "admin"],
          showCount: true,
        },
        {
          label: "Awaiting Publish",
          icon: <CheckCircleIcon />,
          path: "ready-to-publish",
          roles: ["chief-editor", "admin"],
          showCount: true,
        },
        {
          label: "Published",
          icon: <NewspaperIcon />,
          path: "live",
          roles: ["chief-editor", "admin"],
          showCount: true,
        },
        {
          label: "Manage Homepage",
          icon: <ViewListIcon />,
          path: "manage-homepage",
          roles: ["chief-editor", "admin"],
        },
        {
          label: "Archived Articles",
          icon: <ArchiveIcon />,
          path: "archived",
          roles: ["co-editor", "chief-editor", "admin"],
        },
        {
          label: "Manage Users",
          icon: <PeopleIcon />,
          path: "manage-users",
          roles: ["chief-editor", "admin"],
        },
      ];
    }

    // Default fallback
    return [];
  };

  const navItems = getNavItems();

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter(
    (item) => !item.roles || (user?.role && item.roles.includes(user.role))
  );

  const getCount = (path: string): number => {
    switch (path) {
      case "drafts":
        return counts.drafts;
      case "in-review":
        return counts.inReview;
      case "ready-to-publish":
        return counts.readyToPublish;
      case "live":
        return counts.published;
      default:
        return 0;
    }
  };

  const handleNavClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    }
  };

  const handleLogout = async () => {
    try {
      // Close drawer on mobile before logging out
      if (isMobile && mobileOpen) {
        setMobileOpen(false);
      }
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const logoUrl = "https://res.cloudinary.com/djbtf9won/image/upload/v1762320179/logox_aanwli.png";

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: [1],
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt="Logo"
            sx={{
              width: "auto",
              height: "64px",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {filteredNavItems.map((item) => {
          const isActive = currentPath === item.path;
          const count = item.showCount ? getCount(item.path) : 0;
          const labelWithCount = item.showCount ? `${item.label} (${count})` : item.label;

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavClick(item.path)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.contrastText,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.contrastText : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={labelWithCount} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          zIndex: { md: theme.zIndex.drawer + 1 },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 }, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              whiteSpace: { xs: "nowrap", sm: "normal" },
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            News Management System
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              minWidth: 0,
              ml: { xs: 0.5, sm: 1 },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: { xs: "100px", sm: "200px" },
              }}
            >
              {user?.displayName || user?.email || "User"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                fontWeight: "bold",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
              }}
            >
              {user?.role || "No role"}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        {/* Mobile drawer - temporary */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer - persistent */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: "100%",
          mt: { xs: "56px", sm: "64px" },
          overflowX: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default HybridNav;

